/**
 * Mini-parseur markdown du RÉSUMÉ d'un rendez-vous — volontairement limité
 * (titres, listes à puces/numérotées, paragraphes, **gras**, *italique*) et qui
 * produit des données, JAMAIS du HTML.
 *
 * Pourquoi pas `MarkdownMath` : il rend via `dangerouslySetInnerHTML` sans
 * assainissement. Le résumé est produit par un LLM à partir de messages qu'un
 * ÉLÈVE peut écrire → une injection de prompt pourrait faire sortir du HTML
 * (`<img onerror=…>`) qui s'exécuterait dans la session du tuteur. Ici tout ce
 * qui n'est pas de la syntaxe reconnue reste du TEXTE, affiché tel quel.
 */

export interface InlineSpan {
  text: string;
  bold?: boolean;
  italic?: boolean;
}

export type SummaryBlock =
  | { type: 'heading'; level: 1 | 2 | 3; spans: InlineSpan[] }
  | { type: 'paragraph'; spans: InlineSpan[] }
  | { type: 'list'; ordered: boolean; start: number; items: InlineSpan[][] };

const INLINE = /(\*\*[^*\n]+\*\*|\*[^*\s][^*\n]*\*)/g;

export function parseInline(text: string): InlineSpan[] {
  const spans: InlineSpan[] = [];
  for (const part of text.split(INLINE)) {
    if (!part) continue;
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      spans.push({ text: part.slice(2, -2), bold: true });
    } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      spans.push({ text: part.slice(1, -1), italic: true });
    } else {
      spans.push({ text: part });
    }
  }
  return spans;
}

const HEADING = /^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/;
const BULLET = /^\s*[-*•]\s+(.*)$/;
const ORDERED = /^\s*(\d{1,3})[.)]\s+(.*)$/;

export function parseSummaryMarkdown(md: string): SummaryBlock[] {
  const blocks: SummaryBlock[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push({ type: 'paragraph', spans: parseInline(paragraph.join(' ')) });
    paragraph = [];
  };

  for (const line of md.replace(/\r\n?/g, '\n').split('\n')) {
    if (line.trim() === '') {
      flushParagraph();
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flushParagraph();
      const level = Math.min(heading[1]!.length, 3) as 1 | 2 | 3;
      blocks.push({ type: 'heading', level, spans: parseInline(heading[2]!) });
      continue;
    }

    const bullet = BULLET.exec(line);
    const ordered = bullet ? null : ORDERED.exec(line);
    if (bullet || ordered) {
      flushParagraph();
      const isOrdered = !!ordered;
      const itemText = isOrdered ? ordered![2]! : bullet![1]!;
      const last = blocks[blocks.length - 1];
      if (last?.type === 'list' && last.ordered === isOrdered) {
        last.items.push(parseInline(itemText));
      } else {
        blocks.push({
          type: 'list',
          ordered: isOrdered,
          start: isOrdered ? Number(ordered![1]) : 1,
          items: [parseInline(itemText)],
        });
      }
      continue;
    }

    paragraph.push(line.trim());
  }
  flushParagraph();
  return blocks;
}

// ── Copie du résumé ───────────────────────────────────────────────────────────
// Deux formats dans le presse-papiers : texte brut propre (pas d'astérisques ni de
// dièses) pour un champ texte, et HTML minimal pour un éditeur riche (mail, Notion,
// Docs) qui conserve gras et listes. Le HTML est CONSTRUIT ici à partir des blocs
// parsés, avec échappement de tout le texte — jamais du HTML issu du LLM.

function spansToText(spans: InlineSpan[]): string {
  return spans.map((s) => s.text).join('');
}

/** Résumé en texte brut lisible : titres et paragraphes tels quels, listes à puces « • » ou numérotées. */
export function summaryToPlainText(md: string): string {
  return parseSummaryMarkdown(md)
    .map((block) => {
      if (block.type === 'list') {
        return block.items
          .map((item, i) => `${block.ordered ? `${block.start + i}.` : '•'} ${spansToText(item)}`)
          .join('\n');
      }
      return spansToText(block.spans);
    })
    .join('\n\n');
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function spansToHtml(spans: InlineSpan[]): string {
  return spans
    .map((s) => {
      const text = escapeHtml(s.text);
      if (s.bold) return `<strong>${text}</strong>`;
      if (s.italic) return `<em>${text}</em>`;
      return text;
    })
    .join('');
}

/** Résumé en HTML minimal (titres en gras, paragraphes, listes) — tout le texte est échappé. */
export function summaryToHtml(md: string): string {
  return parseSummaryMarkdown(md)
    .map((block) => {
      switch (block.type) {
        case 'heading':
          return `<p><strong>${spansToHtml(block.spans)}</strong></p>`;
        case 'paragraph':
          return `<p>${spansToHtml(block.spans)}</p>`;
        case 'list': {
          const items = block.items.map((item) => `<li>${spansToHtml(item)}</li>`).join('');
          return block.ordered ? `<ol start="${block.start}">${items}</ol>` : `<ul>${items}</ul>`;
        }
      }
    })
    .join('');
}
